import { useState, useEffect, useMemo } from 'react';
import { Sliders } from 'react-feather';
import PropTypes from 'prop-types';
import TablaEstandar from './TablaEstandar';
import Loader from '../../layouts/loader/Loader';
import BarraBusqueda from './BarraBusqueda';
import PanelFiltros from './PanelFiltros';
import useTablaEstandar from './useTablaEstandar';
import useTablaServidor from './useTablaServidor';

const SIN_PARAMS = {};
const MAX_FILTROS_RAPIDOS = 6;

// Sin lista propia, los filtros rápidos son las columnas de valores (multi) visibles por defecto.
const filtrosPorDefecto = (columnas) => {
  const ids = columnas
    .filter((c) => c.filter === 'multi' && c.defaultVisible !== false)
    .map((c) => c.id)
    .slice(0, MAX_FILTROS_RAPIDOS);
  return ids.length > 0 ? ids : undefined;
};

const fecha = (valor, hora) => {
  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return valor;
  const opciones = { day: '2-digit', month: '2-digit', year: 'numeric' };
  return hora
    ? d.toLocaleString('es-ES', { ...opciones, hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('es-ES', opciones);
};

/**
 * Pantalla de listado con la tabla estándar de ADMIN, lista para usar: caja de búsqueda,
 * orden y filtros por columna en servidor, columnas, densidad, Excel y tarjetas en móvil.
 * Las preferencias (orden, filtros, columnas…) se guardan por usuario con la clave `tablaKey`.
 */
const TablaServidor = ({
  tablaKey,
  columnas,
  ruta,
  archivo = undefined,
  nombreEntidad,
  placeholder = 'Buscar…',
  params = SIN_PARAMS,
  barraExtra = null,
  onFilaClick = undefined,
  renderAcciones = undefined,
  renderCelda = undefined,
  tituloTarjeta = undefined,
  claveFila = undefined,
  refresco = 0,
  filtrosRapidos: filtrosPropios = undefined,
  panelSuperior = null,
  panelInicial = false,
  filtrosIniciales = undefined,
}) => {
  const filtrosRapidos = useMemo(
    () => filtrosPropios || filtrosPorDefecto(columnas),
    [filtrosPropios, columnas],
  );
  const tabla = useTablaEstandar({ tabla: tablaKey, columnas, filtrosIniciales });
  // El plegado del panel se guarda por usuario con el resto de preferencias de la tabla.
  const panelAbierto = tabla.panelAbierto ?? panelInicial;
  const hayPanel = !!filtrosRapidos || !!panelSuperior;
  const [busqueda, setBusqueda] = useState('');
  const [busquedaAplicada, setBusquedaAplicada] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setBusquedaAplicada(busqueda.trim()), 300);
    return () => clearTimeout(t);
  }, [busqueda]);

  const consulta = useMemo(
    () => ({ ...tabla.consulta, q: busquedaAplicada || undefined }),
    [tabla.consulta, busquedaAplicada],
  );
  const datos = useTablaServidor({
    ruta,
    archivo,
    consulta,
    params,
    habilitado: tabla.ready,
    refresco,
  });

  const celda = (col, fila) => {
    if (col.id === 'actions') return renderAcciones ? renderAcciones(fila) : null;
    const propia = renderCelda?.(col, fila);
    if (propia !== undefined) return propia;
    if (col.filter === 'date') return fila[col.id] ? fecha(fila[col.id], col.hora) : null;
    return fila[col.id];
  };

  // Se exportan las columnas visibles, en su orden, sin la de Acciones.
  const exportar = () =>
    datos.exportar(tabla.columnasVisibles.filter((c) => !c.fixed).map((c) => c.id));

  // Como en Alumnos: hasta tener las preferencias y la primera tanda de datos solo se ve el
  // spinner, y la pantalla aparece entera de una vez (sin pasar por un formato intermedio).
  if (!tabla.ready || datos.rows === null) return <Loader />;

  return (
    <>
      <BarraBusqueda
        busqueda={busqueda}
        onChange={setBusqueda}
        placeholder={placeholder}
        nombreEntidad={nombreEntidad}
        barraExtra={
          <>
            {hayPanel && (
              <button
                type="button"
                className="tbl-btn"
                aria-expanded={panelAbierto}
                onClick={() => tabla.fijarPanelAbierto(!panelAbierto)}
              >
                <Sliders size={13} /> Filtros
                {tabla.filtrosActivos.length > 0 && (
                  <span className="tbl-btn-n">{tabla.filtrosActivos.length}</span>
                )}
              </button>
            )}
            {barraExtra}
          </>
        }
      />
      {panelAbierto && panelSuperior}
      {filtrosRapidos && panelAbierto && (
        <PanelFiltros tabla={tabla} ids={filtrosRapidos} loadFacets={datos.loadFacets} />
      )}
      {datos.exportError && (
        <div role="alert" className="alert alert-danger" style={{ marginTop: '12px' }}>
          {datos.exportError}
        </div>
      )}
      <TablaEstandar
        tabla={tabla}
        filas={datos.rows}
        total={datos.total}
        hayMas={datos.hasMore}
        cargandoMas={datos.isLoadingMore}
        onCargarMas={datos.loadMore}
        onFilaClick={onFilaClick}
        loadFacets={datos.loadFacets}
        onExportar={exportar}
        exportando={datos.isExporting}
        renderCelda={celda}
        tituloTarjeta={tituloTarjeta}
        nombreEntidad={nombreEntidad}
        claveFila={claveFila}
        sinBotonFiltros={hayPanel}
        cargando={datos.isFetching}
        error={datos.fallo}
        onReintentar={datos.reintentar}
      />
    </>
  );
};

TablaServidor.propTypes = {
  tablaKey: PropTypes.string.isRequired,
  columnas: PropTypes.array.isRequired,
  ruta: PropTypes.string.isRequired,
  archivo: PropTypes.string,
  nombreEntidad: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  params: PropTypes.object,
  barraExtra: PropTypes.node,
  onFilaClick: PropTypes.func,
  renderAcciones: PropTypes.func,
  renderCelda: PropTypes.func,
  tituloTarjeta: PropTypes.func,
  claveFila: PropTypes.func,
  refresco: PropTypes.number,
  filtrosRapidos: PropTypes.array,
  panelSuperior: PropTypes.node,
  panelInicial: PropTypes.bool,
  filtrosIniciales: PropTypes.object,
};

export default TablaServidor;
